import React, { useMemo } from 'react';
import type { ProtocolRecord, DefiEntry, WizardState } from '../types';
import type { Action } from '../state/configReducer';
import SectionCombobox from './SectionCombobox';
import AttachmentList from './AttachmentList';
import { uniqueProtocolSections } from '../state/configReducer';

interface Props {
  protocol: ProtocolRecord;
  state: WizardState;
  hospital: string;
  dispatch: React.Dispatch<Action>;
  onError: (msg: string) => void;
  onDeleteProtocol: () => void;
}

const ProtocolEditor: React.FC<Props> = ({ protocol, state, hospital, dispatch, onError, onDeleteProtocol }) => {
  const sectionOptions = useMemo(() => uniqueProtocolSections(state.data), [state.data]);
  const defi: DefiEntry[] = state.data.defibrillation[protocol.id] ?? [];

  const updateField = <K extends keyof ProtocolRecord>(field: K, value: ProtocolRecord[K]) => {
    dispatch({ type: 'UPDATE_PROTOCOL', id: protocol.id, patch: { [field]: value } as Partial<ProtocolRecord> });
  };

  const updateDefi = (next: DefiEntry[]) => {
    dispatch({ type: 'SET_DEFI', protocolId: protocol.id, defi: next });
  };

  return (
    <form className="resus-admin__form" onSubmit={(e) => e.preventDefault()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0 }}>{protocol.name || 'פרוטוקול חדש'}</h2>
          <span className="resus-admin__subtle">מזהה: <code>{protocol.id}</code></span>
        </div>
        <button type="button" className="resus-admin__btn resus-admin__btn--danger" onClick={onDeleteProtocol}>
          מחק פרוטוקול
        </button>
      </div>

      <div className="resus-admin__field">
        <label className="resus-admin__field-label" htmlFor={`protocol-name-${protocol.id}`}>שם הפרוטוקול</label>
        <input
          id={`protocol-name-${protocol.id}`}
          className="resus-admin__input"
          type="text"
          value={protocol.name}
          onChange={(e) => updateField('name', e.target.value)}
          placeholder='למשל: דום לבבי (cardiac arrest)'
        />
      </div>

      <div className="resus-admin__field">
        <label className="resus-admin__field-label" htmlFor={`protocol-section-${protocol.id}`}>קטגוריה</label>
        <p className="resus-admin__field-hint">
          קלידו או בחרו מהרשימה. ניתן להזין שם קטגוריה חדש — הוא יופיע אוטומטית גם בפרוטוקולים אחרים.
        </p>
        <SectionCombobox
          inputId={`protocol-section-${protocol.id}`}
          value={protocol.section}
          options={sectionOptions}
          onChange={(value) => updateField('section', value)}
          placeholder='למשל: PALS / טראומה'
        />
      </div>

      <div className="resus-admin__field">
        <label className="resus-admin__field-label" htmlFor={`protocol-slug-${protocol.id}`}>תווית קצרה (slug)</label>
        <p className="resus-admin__field-hint">לתצוגה בלבד, לא משנה את המזהה הפנימי.</p>
        <input
          id={`protocol-slug-${protocol.id}`}
          className="resus-admin__input"
          type="text"
          value={protocol.slug || ''}
          onChange={(e) => updateField('slug', e.target.value)}
        />
      </div>

      <AttachmentList
        hospital={hospital}
        attachments={protocol.attachments}
        onChange={(next) => updateField('attachments', next)}
        onError={onError}
      />

      <details>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
          הגדרות דפיברילציה (אופציונלי, בעיקר עבור CPR / טכיקרדיה)
        </summary>
        <div style={{ marginTop: 8 }}>
          {defi.length === 0 && <p className="resus-admin__subtle">אין רמות אנרגיה מוגדרות.</p>}
          {defi.map((entry, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                className="resus-admin__input"
                type="text"
                placeholder="שם הרמה"
                value={entry.name}
                onChange={(e) => updateDefi(defi.map((d, i) => i === idx ? { ...d, name: e.target.value } : d))}
              />
              <input
                className="resus-admin__input"
                type="number"
                placeholder='J/kg'
                value={entry.joulePerKg}
                style={{ maxWidth: 120 }}
                onChange={(e) => updateDefi(defi.map((d, i) => i === idx ? { ...d, joulePerKg: Number(e.target.value) } : d))}
              />
              <button
                type="button"
                className="resus-admin__delete-btn"
                onClick={() => updateDefi(defi.filter((_, i) => i !== idx))}
              >
                מחק
              </button>
            </div>
          ))}
          <button
            type="button"
            className="resus-admin__btn resus-admin__btn--ghost"
            onClick={() => updateDefi([...defi, { name: '', joulePerKg: 2 }])}
            style={{ marginTop: 4 }}
          >
            + הוסף רמה
          </button>
        </div>
      </details>
    </form>
  );
};

export default ProtocolEditor;
